import { useCallback, useEffect, useState } from 'react';
import type { ScanResult } from '../lib/elm327.ts';
import { api, type RecallLookup, type ScanSummary, type Vehicle, type Verdict } from '../lib/api.ts';
import { summarise } from '../lib/summary.ts';
import { usePrefs } from '../lib/prefs.tsx';

/**
 * What the scan means, for someone who is not a technician.
 *
 * This sits above every other panel because it answers the question people
 * actually arrive with: is this car alright, and what do I do now. The panels
 * below remain the evidence, and each finding carries its technical basis so
 * the summary can be checked rather than taken on faith.
 *
 * Written by Claude on the bridge, where the API key lives. A Wi-Fi adapter
 * has no internet route, so the deterministic rules in lib/summary.ts stand in
 * whenever the model cannot be reached — a scan run in a driveway still gets a
 * plain-language read, just a blunter one.
 */
export function SummaryPanel({
  result,
  vehicle,
  scanId,
  action,
}: {
  result: ScanResult;
  vehicle: Vehicle | null;
  scanId: number | null;
  /** Rendered under the headline, before the findings. */
  action?: React.ReactNode;
}) {
  const { units } = usePrefs();
  const [summary, setSummary] = useState<ScanSummary | null>(null);
  const [recalls, setRecalls] = useState<RecallLookup | null>(null);
  const [loading, setLoading] = useState(false);
  // Open by default: the headline is the reason this panel sits at the top.
  const [open, setOpen] = useState(true);

  useEffect(() => {
    if (!vehicle?.vin) {
      setRecalls(null);
      return;
    }
    let cancelled = false;
    api
      .recalls(vehicle.vin)
      .then((r) => !cancelled && setRecalls(r))
      .catch(() => !cancelled && setRecalls(null));
    return () => {
      cancelled = true;
    };
  }, [vehicle?.vin]);

  const load = useCallback(
    async (refresh: boolean) => {
      if (!scanId) return;
      setLoading(true);
      try {
        setSummary(await api.scanSummary(scanId, refresh, units));
      } catch {
        setSummary(null);
      } finally {
        setLoading(false);
      }
    },
    // Units are written into the sentences, so changing the preference needs a
    // summary written in the new one rather than a reformat of this one.
    [scanId, units],
  );

  useEffect(() => {
    setSummary(null);
    void load(false);
  }, [load]);

  // The written summary when we have one, the rule-based read when we do not.
  const usingFallback = !summary || summary.source !== 'claude';
  const shown = usingFallback ? summarise(result, recalls, units) : summary;
  const findings = shown.findings;
  const panelId = `summary-body-${scanId ?? 'live'}`;

  return (
    <div className={`card summary verdict-${shown.verdict}`}>
      <div className="summary-head">
        <div className="row wrap" style={{ gap: 8 }}>
          <span className="eyebrow" style={{ margin: 0, flex: 1 }}>
            Summary
          </span>
          {loading && <span className="pill">writing…</span>}
          {!loading && !usingFallback && open && (
            <button className="tiny ghost" onClick={() => load(true)} title="Generate a fresh summary">
              regenerate
            </button>
          )}
          <button
            className="tiny ghost"
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            aria-controls={panelId}
            title={open ? 'Collapse the summary' : 'Expand the summary'}
          >
            {open ? 'collapse' : `${findings.length} finding${findings.length === 1 ? '' : 's'}`}
          </button>
        </div>
        {/* The headline stays visible when collapsed. Hiding it would leave a
            bar that says nothing, which is worse than the space it saves. */}
        <h3 className="headline">{shown.headline}</h3>
      </div>

      {/* Between the verdict and the evidence: the headline says what is
          wrong, and this is what to do about it, before the detail that
          supports it. */}
      {action && open && <div className="summary-action">{action}</div>}

      <div id={panelId} hidden={!open}>
      {usingFallback && summary?.notice && (
        <div className="banner warn small" style={{ marginBottom: 12 }}>
          {summary.notice} Showing the built-in read instead, which is blunter but always available.
        </div>
      )}

      <div className="findings">
        {findings.map((f, i) => (
          <div className={`finding sev-${f.severity}`} key={`${f.title}-${i}`}>
            {/* A mark and a word alongside the colour, so severity survives a
                greyscale print or a colour-vision difference. */}
            <span className="badge" aria-hidden="true">
              {f.severity === 'critical' ? '!' : f.severity === 'attention' ? '▲' : f.severity === 'watch' ? '•' : '✓'}
            </span>
            <div className="body">
              <p className="title">
                {f.title}
                <span className="sr-only"> — {label(f.severity)}</span>
              </p>
              {/* Three segments rather than a number: this ranks findings
                  against each other, it does not measure anything. The word
                  carries the meaning; the bars only make the ordering visible
                  at a glance. */}
              <p className="urgency">
                <span className="meter" aria-hidden="true">
                  {[0, 1, 2].map((i) => (
                    <i key={i} className={i < URGENCY[f.severity].filled ? 'on' : ''} />
                  ))}
                </span>
                <span className="word">{URGENCY[f.severity].label}</span>
                <span className="when">{URGENCY[f.severity].when}</span>
              </p>
              <p className="detail">{f.detail}</p>
              {f.evidence && <p className="evidence mono">{f.evidence}</p>}
            </div>
          </div>
        ))}
      </div>

      <p className="small dim" style={{ marginBottom: 0, marginTop: 12 }}>
        Based on what the vehicle reported today, and it cannot see anything the car does not monitor itself. Not a
        substitute for an inspection on a lift.
        {usingFallback ? ' Written from a fixed rule set.' : ' Written by Concourse Works from the readings below.'}
      </p>
      </div>
    </div>
  );
}

/**
 * Severity as urgency, which is the question people are actually asking.
 *
 * The timeframes are not invented here. They are the definitions the model is
 * given in the system prompt - critical means deal with it before driving far,
 * attention means repair but not urgently, watch means keep an eye on it - so
 * showing them keeps the label and the model's intent in step rather than
 * layering a second opinion on top.
 */
const URGENCY: Record<Verdict, { label: string; when: string; filled: number }> = {
  critical: { label: 'Urgent', when: 'before driving far', filled: 3 },
  attention: { label: 'Soon', when: 'book it in', filled: 2 },
  watch: { label: 'Monitor', when: 'no action needed yet', filled: 1 },
  good: { label: 'Clear', when: 'nothing to do', filled: 0 },
};

function label(v: Verdict): string {
  return { critical: 'urgent', attention: 'needs action', watch: 'keep an eye on it', good: 'all clear' }[v];
}
